import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/pages/ClientDetail.js (button "+ New receipt request" lives here,
//     not on the dashboard)
//   - src/pages/NewRequest.js (delivery method toggle, submit button labels
//     "Create & send email" / "Create & copy link"; auto-detect goes to
//     step 2 directly, mapper only when auto-detect fails; "Notes for client"
//     textarea on the create form; reminder override available at create-time)
//   - src/lib/csvParser.js (only Xero/Sage/QB/FreeAgent auto-detected; banks
//     and other formats fall back to custom mapper)
//   - src/pages/Settings.js (DEFAULT_REMINDER_DAYS = 7; reminders enabled
//     default true)
//   - src/pages/RequestDetail.js (per-request reminder card; the "Send email"
//     button re-sends the *request* email, NOT a separate reminder template)
//   - find_due_reminders SQL: only one auto-reminder per request (atomic claim)
// Removed:
//   - "Send to your own email first" preview feature -- does not exist
//   - "Sent, awaiting client" status -- there is no such status string;
//     status is computed as Outstanding/Completed
//   - "Send a manual reminder at any time" -- the button re-sends the
//     original request email, not a separate reminder
//   - Auto-detect described as always asking for mapping -- it only asks
//     when auto-detect cannot identify the format
// Added (Pass 1):
//   - Delivery method toggle (Send email / Copy link)
//   - Per-request reminder card controls
//   - Honest description of what "Send email" actually does on an open request
// Added (Pass 2):
//   - "Notes for client" textarea step in the create flow
//   - Reminder override is available at create-time, not only afterwards

export default {
  title:   'Sending your first receipt request',
  summary: 'From a list of transactions to your client opening their upload link, step by step.',
  body: (
    <>
      <P>
        A request is a list of transactions you need receipts for, sent to
        a specific client as a single link. The client opens the link,
        uploads a receipt or invoice against each transaction, and you get
        an email when they're done.
      </P>

      <H>What you'll need first</H>
      <UL>
        <Li>A client added to Receipt Chaser (see <Link to="/support/adding-your-first-client">Adding your first client</Link>)</Li>
        <Li>A CSV of the transactions you want receipts for, exported from your accounting software or bank</Li>
      </UL>

      <H>Step by step</H>
      <OL>
        <Li>
          From the dashboard, click the client you want to chase to open
          their detail page.
        </Li>
        <Li>
          Click <Code>+ New receipt request</Code> at the top right.
        </Li>
        <Li>
          Upload your CSV. Receipt Chaser auto-detects exports from
          <strong> Xero, Sage, QuickBooks, and FreeAgent</strong>. For any
          other format (bank exports, credit card statements, custom
          spreadsheets), Receipt Chaser falls back to a column-mapping
          screen where you tell it which column is the date, description,
          and amount. See{' '}
          <Link to="/support/importing-transactions">Importing transactions</Link>
          {' '}for details.
        </Li>
        <Li>
          Give the request a title. Something descriptive helps both you
          and your client. Examples: <em>October expenses</em>,{' '}
          <em>Q3 missing receipts</em>, <em>Year-end clean-up 2026</em>.
          If you leave it blank, Receipt Chaser auto-fills it with{' '}
          <em>Receipt request for [today's date]</em>.
        </Li>
        <Li>
          Review the transactions. Remove any rows that shouldn't be in
          the request by clicking the ✕ next to the row. Description
          editing isn't available at this stage; if a bank's wording is
          cryptic and you want to clean it up, you can do that on the
          request page after sending (see{' '}
          <Link to="/support/resending-or-editing-a-request">Resending or editing a request</Link>).
        </Li>
        <Li>
          Optionally, add a <strong>note for your client</strong>. This
          appears at the top of their upload page. Useful for
          context-specific instructions like "Please prioritise the
          items over £500" or "Anything missing, mark as lost with a
          note about where it went".
        </Li>
        <Li>
          Optionally, adjust the reminder for this request. By default
          it inherits your practice-wide reminder schedule, but you can
          turn it off for this request only, or set a different
          schedule. The same controls are available later on the
          request page, so you can change your mind after sending.
        </Li>
        <Li>
          Choose how to deliver the request. Two options:
          <UL>
            <Li>
              <strong>Send email automatically.</strong> Receipt Chaser
              emails the client directly. This is the default and works
              for most cases.
            </Li>
            <Li>
              <strong>Copy link to send myself.</strong> Useful if you
              prefer to send the link through WhatsApp, your own email
              client, or whatever channel the client actually responds
              to.
            </Li>
          </UL>
        </Li>
        <Li>
          Click <Code>✓ Create & send email</Code> (or <Code>✓ Create & copy link</Code>{' '}
          if you chose the link option). The request is saved and the
          email is sent (or the link copied to your clipboard).
        </Li>
      </OL>

      <Screenshot
        src="/support-screenshots/03 - new request.png"
        alt="Step 2 of the new receipt request flow, showing the request title field, the optional notes for client textarea, and the auto-reminder toggle. A green banner at the top confirms the CSV was detected as a Xero export with 12 transactions."
        caption="Step 2 of the new request flow: title, notes for client, and the reminder toggle. The banner at the top confirms how Receipt Chaser parsed your CSV."
      />

      <H>What the client sees</H>
      <P>
        The client gets an email from your practice with a link. Clicking
        it opens a page listing every transaction. Against each
        transaction they can upload a file from their device, take a
        photo on a phone, or mark the transaction as a lost receipt with
        a brief explanation. They don't need to create an account or
        remember a password.
      </P>

      <Screenshot
        src="/support-screenshots/03 - client view.png"
        alt="The client's upload page, with the Receipt Chaser logo at the top, a Need help link, an Upload all your receipts at once zone, a progress bar, and a table of transactions each with their own Upload receipt and Mark as lost options."
        caption="The client's view: a single upload zone at the top, a progress bar, and the transaction list below with upload and 'mark as lost' options on each row."
      />

      <P>
        They can stop and come back later. Their progress is tied to the
        link they were sent, so they pick up where they left off.
      </P>

      <H>How chasing works after the request is sent</H>
      <P>
        If the client hasn't completed the request, Receipt Chaser sends
        them <strong>one</strong> automatic reminder. By default the
        reminder goes after seven days, but you can change the schedule
        and customise the reminder text from{' '}
        <Code>Settings → Auto-reminders</Code>. Reminders are on by
        default for new practices.
      </P>

      <Screenshot
        src="/support-screenshots/03 - reminder-settings-page.jpg"
        alt="The Settings page with the Auto-reminders tab selected, showing the on/off toggle, the days-after-send field, the available tokens reference, and the editable subject and body templates."
        caption="The Auto-reminders tab in Settings, where you set the practice-wide reminder schedule and customise the reminder text."
      />

      <P>
        Each request can also override the practice-wide schedule, or
        skip the reminder altogether, from the reminder card on the
        request page. The same card shows the date the reminder is
        scheduled to fire (or that it has already been sent).
      </P>

      <Screenshot
        src="/support-screenshots/03 - reminder-emails-header.jpg"
        alt="The top of an open receipt request, with Send email, Copy client link, Cancel request, and Delete request buttons in the header, and the Auto-reminder card below showing the scheduled date."
        caption="On the request page, the action buttons at the top let you re-send the email or copy the link, and the Auto-reminder card shows when the reminder will fire."
      />

      <Callout variant="info" title="Re-sending the request">
        From an open request you can click <Code>📧 Send email</Code> at
        the top to send the original request email again. This sends
        the same request email (using your normal request template),
        not a separate reminder. It's useful when a client says they
        never received the original.
      </Callout>

      <H>What happens when the client is done</H>
      <P>
        When every transaction has either a file or a lost-receipt
        declaration, the request is treated as complete. If you have
        "Notify me when a client completes a request" turned on in{' '}
        <Code>Settings → Email template</Code> (default), you'll get an
        email saying so.
      </P>
      <P>
        From the request page you can download every file as a single
        zip, with each file renamed to match its transaction date and
        description. See{' '}
        <Link to="/support/reviewing-completed-receipts">Reviewing completed receipts</Link>
        {' '}for what to do next.
      </P>

      <Callout variant="info" title="The client doesn't have to finish in one go">
        Most clients upload in two or three sessions. You can see partial
        progress at any time on the request page, so you always know what
        they've done and what's still outstanding.
      </Callout>
    </>
  ),
}
